import { Card, Row } from 'antd';
import i18next from 'i18next';
import * as Moment from 'moment';
import React, { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ENV } from 'src/';
import ExportButtonWithModal from 'src/common/components/export-button-with-modal';
import { AutocompleteTextFilter } from 'src/common/components/table-filters/autocomplete-text-filter';
import { DateRangeFilter } from 'src/common/components/table-filters/date-range-filter';
import { OptionsFilter } from 'src/common/components/table-filters/options-filter';
import { emptyTableModel, getDateFormat } from 'src/common/utils';
import { AppConfiguration } from 'src/core/services/configuration.service';
import { formatMessage } from 'src/core/services/http.service';
import { Query } from 'src/core/stores/data-store';
import { TableModel, TableView } from 'src/core/ui/collections/table';
import { setHeader, setRoutes } from 'src/core/ui/shell';
import { Languages } from 'src/i18n';
import { AppConfig, container } from 'src/inversify.config';
import {
  getStructmasterLicenseLogsTableHeaders,
  StructmasterLicenseLogCsvReportRequest,
  StructmasterLicenseLogsStore,
  StructmasterLicenseLogSummary,
  StructmasterLicenseLogType,
  StrutcMasterLicenseLogSummaryCsvRequest,
} from 'src/stores/licensing-logs-store';

const StructmasterLicenseLogsPage: FC = () => {
  const { t } = useTranslation();
  const config: AppConfiguration = useContext(AppConfig);

  const [query, setQuery] = useState({
    searchQuery: '',
    orderBy: [{ field: 'CreatedOn', direction: 'Descending', useProfile: false }],
    skip: 0,
    take: 10,
  } as Query);

  const licenseLogsStore = container.get(StructmasterLicenseLogsStore);
  const licenseLogsState = licenseLogsStore.state;

  setHeader(t('StructmasterLicenseLogs'));
  setRoutes([
    { path: '', breadcrumbName: t('Home') },
    { path: 'licenses', breadcrumbName: t('Licenses') },
    { path: 'logs', breadcrumbName: t('StructmasterLicenseLogs') },
  ]);

  useEffect(
    () => {
      licenseLogsStore.load(query);
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const itemTemplate: StructmasterLicenseLogCsvReportRequest = {
    headers: getStructmasterLicenseLogsTableHeaders(t),
    csvSeparator: ',',
    currentLanguage: i18next.language !== Languages.Russian ? i18next.language : 'ru-RU',
    fileName: `license_logs.csv`,
    items: [],
  };

  const handleExportOk = async (separator: string) => {
    let tempItemModal = itemTemplate;
    tempItemModal.csvSeparator = separator || tempItemModal.csvSeparator;
    tempItemModal.items = licenseLogsState.items.value.map((x) => {
      return {
        createdOn: x.createdOn,
        logType: t(x.logType),
        userName: x.userName,
        description: x.description,
        organization: x.organizationName,
        licenseName: x.name,
      } as StrutcMasterLicenseLogSummaryCsvRequest;
    });

    await licenseLogsStore.export(tempItemModal);
  };

  const tableModel = {
    query,
    columns: [
      {
        searchable: true,
        applyPartialFilter: true,
        filter: <AutocompleteTextFilter baseUrl={`${licenseLogsStore.baseUrl}/get-logs`} fieldName={'name'} fieldValue={'name'} />,
        sortable: true,
        sortField: 'name',
        field: 'name',
        title: t('LicenseName'),
        width: '15%',
        renderer: (data) => <span>{data.name}</span>,
      },
      {
        searchable: true,
        applyPartialFilter: true,
        filter: (
          <AutocompleteTextFilter baseUrl={`${licenseLogsStore.baseUrl}/get-logs`} fieldName={'organizationName'} fieldValue={'organizationName'} />
        ),
        sortable: true,
        sortField: 'organizationName',
        field: 'organizationName',
        title: t('Organization'),
        width: '15%',
        renderer: (data) => <span>{data.organizationName}</span>,
      },
      {
        sortable: true,
        filter: <DateRangeFilter />,
        fiterField: 'createdOn',
        field: 'createdOn',
        title: t('CreatedOn'),
        width: '15%',
        renderer: (data) => <span>{Moment.utc(data.createdOn).local().locale(i18next.language).format(`${getDateFormat()}, hh:mm a`)}</span>,
      },
      {
        sortable: true,
        sortField: 'logType',
        field: 'logType',
        title: t('LogType'),
        width: '15%',
        filter: <OptionsFilter options={Object.entries(StructmasterLicenseLogType).map(([key, value]) => ({ label: t(key), value: value }))} />,
        renderer: (data) => <span>{t(data.logType)}</span>,
      },
      {
        searchable: true,
        applyPartialFilter: true,
        filter: <AutocompleteTextFilter baseUrl={`${licenseLogsStore.baseUrl}/get-logs`} fieldName={'userName'} fieldValue={'userName'} />,
        sortable: true,
        sortField: 'userName',
        field: 'userName',
        title: t('User'),
        width: '15%',
        renderer: (data) => <span>{data.userName}</span>,
      },
      {
        searchable: true,
        field: 'description',
        title: t('Description'),
        renderer: (data) => <span>{data.description}</span>,
      },
    ],
    data: { ...licenseLogsState.value, isBusy: licenseLogsState.value.isBusy },
  } as TableModel<StructmasterLicenseLogSummary>;

  return (
    <Row align="top" justify="space-between">
      <Card className="card-table" style={{ width: '100%' }}>
        <TableView
          rowKey="id"
          canCreateNew={false}
          canDelete={false}
          canEdit={false}
          canSort
          onQueryChanged={(query: Query) => {
            setQuery(query);
            licenseLogsStore.load(query);
          }}
          onRefresh={() => licenseLogsStore.load(query)}
          model={licenseLogsState.errorMessage.value ? emptyTableModel : tableModel}
          error={
            licenseLogsState.errorMessage.value &&
            (config.environment === ENV.DEV ? formatMessage(licenseLogsState.errorMessage.value) : t('LicenseLogsTableError'))
          }
          rightToolbar={
            <li>
              <ExportButtonWithModal onExport={handleExportOk} text={t('ExportCSV')} />
            </li>
          }
        />
      </Card>
    </Row>
  );
};

export default StructmasterLicenseLogsPage;
